/* eslint-disable react-hooks/exhaustive-deps, ts/consistent-type-imports, react-compiler/react-compiler */
import type { ISurveyTranslationRow } from '@/features/survey/survey-translation-section'
import { useNavigate, useParams } from '@tanstack/react-router'
import { generatePath } from '@/shared/libs/generate-path'
import { ArrowLeft, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useCreateSurveyQuestionMutation,
  useDeleteSurveyQuestionMutation,
  useSurveyQuestionByIdQuery,
  useSurveyQuestionsQuery,
  useSurveyVersionByIdQuery,
  useUpdateSurveyQuestionMutation,
} from '@/entities/survey'
import { useTenantByIdQuery, useTenantLanguages } from '@/entities/tenant'

import { TRANSLATION_JOB_SCOPE } from '@/shared/constants/status'
import {
  localizedDataToValuesByLanguage,
  mergeSurveyTranslationRows,
  surveyEditorLanguageCodes,
  surveyLanguagesFromTenantLanguages,
  surveyTranslationsToLocalizedData,
} from '@/features/survey/lib/survey-localized-mapper'
import { isSurveyQuestionDeleted } from '@/features/survey/lib/survey-question-helpers'
import { SurveyTranslationSection } from '@/features/survey/survey-translation-section'
import { DEFAULT_PAGE_SIZE, SURVEY_QUESTION_TYPES, SurveyQuestionType } from '@/shared/constants'
import { useDashboardDisplayLanguage } from '@/shared/hooks/use-dashboard-display-language'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { showToast } from '@/shared/libs/toast'
import { resolveDashboardLocalizedValue } from '@/shared/utils/dashboard-localized-value'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card'
import { FormField } from '@/shared/ui/form-field'
import { Input } from '@/shared/ui/input'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Skeleton } from '@/shared/ui/skeleton'
import { ConfirmDialog } from '@/shared/ui/confirm-dialog'
import { useCreateTranslationDraft } from '@/features/localized-translation'

interface PersistedSurveyQuestionDraft {
  questionType: SurveyQuestionType
  orderIndex: number
  translations: ISurveyTranslationRow[]
}

function isSurveyQuestionType(value: string): value is SurveyQuestionType {
  return Object.values(SURVEY_QUESTION_TYPES).includes(value as SurveyQuestionType)
}

export function SurveyQuestionPage() {
  const { t } = useTranslation()
  const { activeLanguage } = useDashboardDisplayLanguage()
  const navigate = useNavigate()
  const scopedAdminPath = useScopedAdminPath()
  const params = useParams({ strict: false })
  const tenantId = params?.tenantId ?? ''
  const versionId = (params?.versionId as string) ?? ''
  const questionId = (params?.questionId as string) ?? ''
  const isCreateMode = !questionId || questionId === 'create'

  const { data: tenantResponse, isLoading: tenantLoading } = useTenantByIdQuery(tenantId)
  const tenantName = tenantResponse?.data?.name ?? ''

  const versionQuery = useSurveyVersionByIdQuery(versionId, { enabled: !!versionId })
  const versionTitle = useMemo(() => {
    const v = versionQuery.data?.data
    if (!v?.localizedData) return versionId
    return resolveDashboardLocalizedValue(v.localizedData, activeLanguage)
  }, [activeLanguage, versionQuery.data?.data, versionId])

  const questionQuery = useSurveyQuestionByIdQuery(questionId, {
    enabled: !isCreateMode && !!questionId,
  })
  const question = questionQuery.data?.data
  const questionDeleted = question ? isSurveyQuestionDeleted(question) : false

  const tenantLanguagesQuery = useTenantLanguages(tenantId)
  const tenantLanguages = useMemo(
    () => tenantLanguagesQuery.items ?? [],
    [tenantLanguagesQuery.items],
  )
  const languageCodes = useMemo(
    () => surveyLanguagesFromTenantLanguages(tenantLanguages),
    [tenantLanguages],
  )
  const questionEditorLanguageCodes = useMemo(
    () => surveyEditorLanguageCodes(languageCodes, question?.localizedData),
    [languageCodes, question?.localizedData],
  )
  const createQuestionOrderQuery = useSurveyQuestionsQuery(
    tenantId,
    versionId,
    { current: 1, pageSize: DEFAULT_PAGE_SIZE },
    { enabled: isCreateMode },
  )
  const nextQuestionOrderIndex = useMemo(() => {
    const rows = createQuestionOrderQuery.data?.data ?? []
    if (rows.length === 0) return 1
    return Math.max(...rows.map((row) => row.orderIndex)) + 1
  }, [createQuestionOrderQuery.data?.data])

  const updateQuestionMutation = useUpdateSurveyQuestionMutation()
  const createQuestionMutation = useCreateSurveyQuestionMutation()
  const deleteQuestionMutation = useDeleteSurveyQuestionMutation()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const [questionType, setQuestionType] = useState<SurveyQuestionType>(
    SURVEY_QUESTION_TYPES.SINGLE_ANSWER,
  )
  const [orderIndex, setOrderIndex] = useState(1)
  const [questionTranslations, setQuestionTranslations] = useState<ISurveyTranslationRow[]>([])
  const questionTranslationEntityId = isCreateMode
    ? `survey-question-create:${versionId}`
    : `survey-question:${questionId}`

  const questionDraft = useMemo(
    () =>
      ({
        questionType,
        orderIndex,
        translations: questionTranslations,
      }) satisfies PersistedSurveyQuestionDraft,
    [orderIndex, questionTranslations, questionType],
  )
  const hasQuestionDraftContent = useMemo(
    () =>
      questionType !== SURVEY_QUESTION_TYPES.SINGLE_ANSWER ||
      orderIndex !== nextQuestionOrderIndex ||
      questionTranslations.some((row) => !!(row.name.trim() || row.description.trim())),
    [nextQuestionOrderIndex, orderIndex, questionTranslations, questionType],
  )

  useEffect(() => {
    if (isCreateMode) return
    if (!question) return
    if (isSurveyQuestionType(question.type)) setQuestionType(question.type)
    setOrderIndex(question.orderIndex)
  }, [isCreateMode, question])

  const createQuestionHydrateKey = questionEditorLanguageCodes.join('\0')

  useEffect(() => {
    if (isCreateMode || !question?.id) return
    const codes = surveyEditorLanguageCodes(languageCodes, question.localizedData)
    if (codes.length === 0) {
      setQuestionTranslations([])
      return
    }
    const values = localizedDataToValuesByLanguage(question.localizedData, languageCodes, {
      primaryField: 'question',
    })
    setQuestionTranslations(
      codes.map((language) => ({
        language,
        name: values[language]?.name ?? '',
        description: values[language]?.description ?? '',
      })),
    )
  }, [isCreateMode, languageCodes, question?.id, question?.localizedData])

  const hydrateCreateDraft = useCallback(
    (persistedDraft?: PersistedSurveyQuestionDraft) => {
      setQuestionType(persistedDraft?.questionType ?? SURVEY_QUESTION_TYPES.SINGLE_ANSWER)
      setOrderIndex(persistedDraft?.orderIndex ?? nextQuestionOrderIndex)
      setQuestionTranslations(
        mergeSurveyTranslationRows(questionEditorLanguageCodes, persistedDraft?.translations),
      )
    },
    [nextQuestionOrderIndex, questionEditorLanguageCodes],
  )
  const { clearCurrentDraft, clearCurrentJobs } = useCreateTranslationDraft({
    enabled: isCreateMode,
    scope: TRANSLATION_JOB_SCOPE.SURVEY_QUESTION,
    entityId: questionTranslationEntityId,
    audioEnabled: true,
    initSignature: `create:${versionId}:${createQuestionHydrateKey}:order:${nextQuestionOrderIndex}`,
    draft: questionDraft,
    hasContent: hasQuestionDraftContent,
    onHydrate: hydrateCreateDraft,
  })

  const questionEditorFields = useMemo(
    () => [
      {
        key: 'name' as const,
        label: t('survey.fieldTitle', { defaultValue: 'Title' }),
        placeholder: t('survey.fieldQuestionTitlePlaceholder', { defaultValue: 'Question text' }),
      },
      {
        key: 'description' as const,
        label: t('survey.fieldDescription', { defaultValue: 'Description' }),
        placeholder: t('survey.fieldQuestionDescriptionPlaceholder', { defaultValue: 'Help text' }),
        multiline: true,
        rows: 3,
      },
    ],
    [t],
  )

  const handleSaveQuestion = useCallback(async () => {
    if (!versionId) return
    try {
      const localizedData = surveyTranslationsToLocalizedData(questionTranslations, {
        primaryField: 'question',
      })
      if (isCreateMode) {
        if (Object.keys(localizedData).length < 1) {
          showToast.error(
            t('survey.createQuestionLocalizedRequired', {
              defaultValue: 'Please enter localized title or description before creating question.',
            }),
          )
          return
        }
        const res = await createQuestionMutation.mutateAsync({
          versionId,
          payload: {
            type: questionType,
            orderIndex,
            localizedData,
          },
        })
        showToast.success(t('survey.createQuestionSuccess', { defaultValue: 'Question created.' }))
        clearCurrentJobs()
        clearCurrentDraft()
        const newId = res.data?.id
        if (newId && tenantId) {
          void navigate({
            href: scopedAdminPath(
              generatePath('/tenant/:tenantId/surveys/:versionId/questions/:questionId', {
                tenantId,
                versionId,
                questionId: newId,
              }),
            ),
          })
        }
        return
      }
      if (!questionId) return
      await updateQuestionMutation.mutateAsync({
        questionId,
        payload: {
          type: questionType,
          orderIndex,
          localizedData: Object.keys(localizedData).length > 0 ? localizedData : null,
        },
      })
      showToast.success(t('common.saved', { defaultValue: 'Saved.' }))
      clearCurrentJobs()
      await questionQuery.refetch()
    } catch {
      showToast.error(
        isCreateMode
          ? t('survey.createQuestionError', { defaultValue: 'Could not create question.' })
          : t('survey.saveQuestionError', { defaultValue: 'Could not save question.' }),
      )
    }
  }, [
    createQuestionMutation,
    clearCurrentDraft,
    clearCurrentJobs,
    isCreateMode,
    navigate,
    orderIndex,
    questionId,
    questionQuery,
    questionTranslations,
    questionType,
    questionTranslationEntityId,
    scopedAdminPath,
    t,
    tenantId,
    updateQuestionMutation,
    versionId,
  ])

  const handleDeleteQuestion = useCallback(async () => {
    if (!tenantId || !questionId || !versionId || deleteQuestionMutation.isPending) return
    try {
      await deleteQuestionMutation.mutateAsync({ questionId, versionId })
      showToast.success(t('survey.deleteQuestionSuccess', { defaultValue: 'Question deleted.' }))
      setShowDeleteConfirm(false)
      void navigate({
        href: scopedAdminPath(
          generatePath('/tenant/:tenantId/surveys/:versionId', { tenantId, versionId }),
        ),
      })
    } catch {
      showToast.error(
        t('survey.deleteQuestionError', { defaultValue: 'Could not delete question.' }),
      )
    }
  }, [deleteQuestionMutation, navigate, questionId, scopedAdminPath, t, tenantId, versionId])

  return (
    <PageContainer>
      <PageHeader
        title={
          isCreateMode
            ? t('survey.createQuestionTitle', { defaultValue: 'New question' })
            : t('survey.questionDetailTitle', { defaultValue: 'Question' })
        }
        breadcrumbs={[
          { label: t('sidebar.tenants'), path: scopedAdminPath('/tenant') },
          ...(tenantId
            ? [
                {
                  label: tenantLoading ? t('common.loading') : tenantName || tenantId,
                  path: scopedAdminPath(generatePath('/tenant/:tenantId', { tenantId })),
                },
              ]
            : []),
          {
            label: t('survey.listTitle'),
            ...(tenantId
              ? { path: scopedAdminPath(generatePath('/tenant/:tenantId/surveys', { tenantId })) }
              : {}),
          },
          {
            label: versionTitle,
            ...(tenantId && versionId
              ? {
                  path: scopedAdminPath(
                    generatePath('/tenant/:tenantId/surveys/:versionId', { tenantId, versionId }),
                  ),
                }
              : {}),
          },
          {
            label: isCreateMode
              ? t('common.create', { defaultValue: 'Create' })
              : t('survey.questionBreadcrumb', { defaultValue: 'Question' }),
          },
        ]}
        actions={
          <div className="flex flex-wrap gap-2">
            {tenantId && versionId ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  void navigate({
                    href: scopedAdminPath(
                      generatePath('/tenant/:tenantId/surveys/:versionId', { tenantId, versionId }),
                    ),
                  })
                }
              >
                <ArrowLeft size={16} />
                {t('common.back')}
              </Button>
            ) : null}

            {!isCreateMode && !!question && !questionDeleted && (
              <>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleteQuestionMutation.isPending}
                >
                  <Trash2 size={16} />
                  {t('common.delete')}
                </Button>
                <ConfirmDialog
                  open={showDeleteConfirm}
                  title={t('survey.deleteQuestionTitle', { defaultValue: 'Delete question?' })}
                  description={t('survey.deleteQuestionDescription', {
                    defaultValue: 'This action cannot be undone.',
                  })}
                  confirmLabel={t('common.delete')}
                  cancelLabel={t('common.cancel')}
                  onConfirm={handleDeleteQuestion}
                  onCancel={() => setShowDeleteConfirm(false)}
                  variant="destructive"
                />
              </>
            )}
          </div>
        }
      />

      {!isCreateMode && questionQuery.isLoading && (
        <div className="mt-6 space-y-3">
          <Skeleton className="h-40 w-full" />
        </div>
      )}

      {!isCreateMode && questionQuery.isError && (
        <Card className="mt-6 border-red-200 bg-red-50/80">
          <CardContent className="pt-4 text-sm text-red-800">
            {t('survey.questionLoadError', { defaultValue: 'Could not load question.' })}
          </CardContent>
        </Card>
      )}

      {(isCreateMode || (!questionQuery.isLoading && question)) && (
        <>
          {!isCreateMode && questionDeleted && (
            <Card className="mt-6 border-amber-200 bg-amber-50/80">
              <CardContent className="pt-4 text-sm text-amber-900">
                {t('survey.questionDeletedReadOnly', {
                  defaultValue:
                    'This question is deleted. You can review content below; editing is disabled.',
                })}
              </CardContent>
            </Card>
          )}

          <Card className="mt-6 border-zinc-200">
            <CardHeader>
              <CardTitle className="text-base">
                {t('survey.questionFormTitle', { defaultValue: 'Question details' })}
              </CardTitle>
              {!isCreateMode && question && (
                <CardDescription className="font-mono text-xs">{question.id}</CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <fieldset
                disabled={questionDeleted}
                className="min-w-0 space-y-6 border-0 p-0 m-0 disabled:opacity-65"
              >
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    label={t('survey.columnQuestionType', { defaultValue: 'Type' })}
                    required
                  >
                    <Select
                      value={questionType}
                      onValueChange={(value) => setQuestionType(value as SurveyQuestionType)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(SURVEY_QUESTION_TYPES).map((type) => (
                          <SelectItem key={type} value={type}>
                            {t(`survey.questionType.${type}`, { defaultValue: type })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField label={t('survey.columnOrder', { defaultValue: 'Order' })} required>
                    <Input
                      type="number"
                      min={1}
                      value={orderIndex}
                      onChange={(e) => setOrderIndex(Number(e.target.value) || 1)}
                    />
                  </FormField>
                </div>
                {questionEditorLanguageCodes.length > 0 ? (
                  <SurveyTranslationSection
                    blockTitle={t('survey.localizedBlockTitle', {
                      defaultValue: 'Localized content',
                    })}
                    languages={questionEditorLanguageCodes}
                    translations={questionTranslations}
                    onChange={setQuestionTranslations}
                    fields={questionEditorFields}
                    entityId={questionTranslationEntityId}
                    entityLabel={
                      isCreateMode
                        ? t('survey.createQuestionTitle', { defaultValue: 'New question' })
                        : t('survey.questionDetailTitle', { defaultValue: 'Question' })
                    }
                    scope={TRANSLATION_JOB_SCOPE.SURVEY_QUESTION}
                    {...(typeof window !== 'undefined'
                      ? { entityPath: window.location.pathname }
                      : {})}
                  />
                ) : (
                  <p className="text-sm text-amber-700">
                    {t('survey.noTenantLanguages', {
                      defaultValue: 'Configure tenant languages to edit localized fields.',
                    })}
                  </p>
                )}
                <Button
                  type="button"
                  onClick={() => void handleSaveQuestion()}
                  disabled={
                    questionDeleted ||
                    createQuestionMutation.isPending ||
                    updateQuestionMutation.isPending
                  }
                >
                  {isCreateMode
                    ? t('common.create', { defaultValue: 'Create' })
                    : t('common.save', { defaultValue: 'Save' })}
                </Button>
              </fieldset>
            </CardContent>
          </Card>
        </>
      )}
    </PageContainer>
  )
}
